import React from 'react'
import { FastField, Field, useFormikContext } from 'formik'
import { Box, FormHelperText, Grid, Typography } from '@mui/material'
import { CustomRadio, CustomRadioGroup, CustomTextField, CustomCheckboxWithoutInitialValue } from 'src/form'

import CustomChipSelect from 'src/form/CustomChipSelect'
import { MedicationForm } from './MedicationForm'
import { Section, SubSection } from '../common/Section'
import { IntakeNoteComprehensiveFormType } from './IntakeNoteComprehensiveFormSchema'

const substanceAbuseOptions = [
  { label: 'None', value: 'NONE' },
  { label: 'Using', value: 'USING' },
  { label: 'Have used', value: 'HAVE_USED' },
]

export function BiopsychosocialEvaluationForm() {
  const { values, errors, setFieldValue } = useFormikContext<IntakeNoteComprehensiveFormType>()

  return (
    <Section title="Biopsychosocial Evaluation">
      <SubSection title="Medical History">
        <Box mt={2}>
          <Grid container columnSpacing={2.5} rowSpacing={0}>
            <Grid item xs={12} md={4}>
              <FastField
                fullWidth
                component={CustomCheckboxWithoutInitialValue}
                name="BIOPYCHOSOCIAL_EVALUATION.MEDICAL_HISTORY.ARTHRITIS"
                label="Arthritis"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FastField
                fullWidth
                component={CustomCheckboxWithoutInitialValue}
                name="BIOPYCHOSOCIAL_EVALUATION.MEDICAL_HISTORY.ASTHMA"
                label="Asthma"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FastField
                fullWidth
                component={CustomCheckboxWithoutInitialValue}
                name="BIOPYCHOSOCIAL_EVALUATION.MEDICAL_HISTORY.AUTOIMMUNE_DISORDER"
                label="Autoimmune disorder"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FastField
                fullWidth
                component={CustomCheckboxWithoutInitialValue}
                name="BIOPYCHOSOCIAL_EVALUATION.MEDICAL_HISTORY.BACK_PROBLEMS"
                label="Back problems"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FastField
                fullWidth
                component={CustomCheckboxWithoutInitialValue}
                name="BIOPYCHOSOCIAL_EVALUATION.MEDICAL_HISTORY.CANCER_CURRENT_OR_PAST"
                label="Cancer (current or past)"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FastField
                fullWidth
                component={CustomCheckboxWithoutInitialValue}
                name="BIOPYCHOSOCIAL_EVALUATION.MEDICAL_HISTORY.CHRONIC_PAIN"
                label="Chronic pain"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FastField
                fullWidth
                component={CustomCheckboxWithoutInitialValue}
                name="BIOPYCHOSOCIAL_EVALUATION.MEDICAL_HISTORY.COPD_EMPHYSEMA"
                label="COPD/emphysema"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FastField
                fullWidth
                component={CustomCheckboxWithoutInitialValue}
                name="BIOPYCHOSOCIAL_EVALUATION.MEDICAL_HISTORY.DIABETES"
                label="Diabetes"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FastField
                fullWidth
                component={CustomCheckboxWithoutInitialValue}
                name="BIOPYCHOSOCIAL_EVALUATION.MEDICAL_HISTORY.HEART_DISEASE"
                label="Heart disease"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FastField
                fullWidth
                component={CustomCheckboxWithoutInitialValue}
                name="BIOPYCHOSOCIAL_EVALUATION.MEDICAL_HISTORY.HIGH_BLOOD_PRESSURE"
                label="High blood pressure"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FastField
                fullWidth
                component={CustomCheckboxWithoutInitialValue}
                name="BIOPYCHOSOCIAL_EVALUATION.MEDICAL_HISTORY.HIGH_CHOLESTEROL"
                label="High cholesterol"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FastField
                fullWidth
                component={CustomCheckboxWithoutInitialValue}
                name="BIOPYCHOSOCIAL_EVALUATION.MEDICAL_HISTORY.HORMONAL_IMBALANCE"
                label="Hormonal imbalance"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FastField
                fullWidth
                component={CustomCheckboxWithoutInitialValue}
                name="BIOPYCHOSOCIAL_EVALUATION.MEDICAL_HISTORY.IBS_CROHNS_COLITIS"
                label="IBS / Crohn's / Colitis"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FastField
                fullWidth
                component={CustomCheckboxWithoutInitialValue}
                name="BIOPYCHOSOCIAL_EVALUATION.MEDICAL_HISTORY.LIVER_DISEASE"
                label="Liver disease"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FastField
                fullWidth
                component={CustomCheckboxWithoutInitialValue}
                name="BIOPYCHOSOCIAL_EVALUATION.MEDICAL_HISTORY.MIGRAINES"
                label="Migraines"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FastField
                fullWidth
                component={CustomCheckboxWithoutInitialValue}
                name="BIOPYCHOSOCIAL_EVALUATION.MEDICAL_HISTORY.SEIZURES"
                label="Seizures"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FastField
                fullWidth
                component={CustomCheckboxWithoutInitialValue}
                name="BIOPYCHOSOCIAL_EVALUATION.MEDICAL_HISTORY.SLEEP_APNEA"
                label="Sleep apnea"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FastField
                fullWidth
                component={CustomCheckboxWithoutInitialValue}
                name="BIOPYCHOSOCIAL_EVALUATION.MEDICAL_HISTORY.STROKE"
                label="Stroke"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FastField
                fullWidth
                component={CustomCheckboxWithoutInitialValue}
                name="BIOPYCHOSOCIAL_EVALUATION.MEDICAL_HISTORY.THYROID_DISORDER"
                label="Thyroid disorder"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FastField
                fullWidth
                component={CustomCheckboxWithoutInitialValue}
                name="BIOPYCHOSOCIAL_EVALUATION.MEDICAL_HISTORY.TRAUMATIC_BRAIN_INJURY"
                label="Traumatic brain injury"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FastField
                fullWidth
                component={CustomCheckboxWithoutInitialValue}
                name="BIOPYCHOSOCIAL_EVALUATION.MEDICAL_HISTORY.ULCERS"
                label="Ulcers"
              />
            </Grid>
          </Grid>
        </Box>

        <Box mt={2}>
          <Typography variant="h4">Allergies</Typography>
          <Field component={CustomRadioGroup} name="BIOPYCHOSOCIAL_EVALUATION.ALLERGIES">
            <CustomRadio label="Participant reports no allergies." value="reports_no" />
            <CustomRadio label="Participant reports having allergies." value="reports_yes" />
          </Field>
          {values.BIOPYCHOSOCIAL_EVALUATION.ALLERGIES === 'reports_yes' && (
            <FastField
              component={CustomTextField}
              name="BIOPYCHOSOCIAL_EVALUATION.ALLERGIES_DETAILS"
              label="Allergies"
              fullWidth
              multiline
              minRows={4}
            />
          )}
        </Box>

        <Box mt={2}>
          <Typography variant="h4">Other Medical Conditions</Typography>
          <Field component={CustomRadioGroup} name="BIOPYCHOSOCIAL_EVALUATION.MEDICAL_CONDITIONS">
            <CustomRadio label="Participant reports no other medical conditions." value="reports_no" />
            <CustomRadio label="Participant reports other medical conditions." value="reports_yes" />
          </Field>
          {values.BIOPYCHOSOCIAL_EVALUATION.MEDICAL_CONDITIONS === 'reports_yes' && (
            <FastField
              component={CustomTextField}
              name="BIOPYCHOSOCIAL_EVALUATION.MEDICAL_CONDITIONS_DETAILS"
              label="Other conditions"
              fullWidth
              multiline
              minRows={4}
            />
          )}
          <FastField
            component={CustomTextField}
            name="BIOPYCHOSOCIAL_EVALUATION.ADDITIONAL_NOTES"
            label="Additional notes (if applicable)"
            fullWidth
            multiline
            minRows={4}
          />
        </Box>
      </SubSection>

      <SubSection title="Medications">
        <Field
          component={CustomRadioGroup}
          name="BIOPYCHOSOCIAL_EVALUATION.MEDICATIONS"
          handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            const value = event.target.value as 'reports_no' | 'reports_yes'

            // Clear the medication details and primary care doctor if the participant reports not taking any prescription medications
            if (value === 'reports_no') {
              setFieldValue('BIOPYCHOSOCIAL_EVALUATION.MEDICATIONS_DETAILS', undefined)
              setFieldValue('BIOPYCHOSOCIAL_EVALUATION.PRIMARY_CARE_DOCTOR', undefined)
            }
          }}
        >
          <CustomRadio label="Participant reports not taking any prescription medications." value="reports_no" />
          <CustomRadio label="Participant reports taking prescription medications." value="reports_yes" />
        </Field>
        {values.BIOPYCHOSOCIAL_EVALUATION.MEDICATIONS === 'reports_yes' && <MedicationForm />}
      </SubSection>
      <SubSection title="Substance Abuse">
        <FastField
          component={CustomChipSelect}
          name="BIOPYCHOSOCIAL_EVALUATION.SUBSTANCE_ABUSE.TOBACCO"
          label="Tobacco (Cigarettes, chew, vape)"
          options={substanceAbuseOptions}
          fullWidth
        />
        <FastField
          component={CustomChipSelect}
          name="BIOPYCHOSOCIAL_EVALUATION.SUBSTANCE_ABUSE.ALCOHOL"
          label="Alcohol"
          options={substanceAbuseOptions}
          fullWidth
        />
        <FastField
          component={CustomChipSelect}
          name="BIOPYCHOSOCIAL_EVALUATION.SUBSTANCE_ABUSE.MARIJUANA"
          label="Marijuana"
          options={substanceAbuseOptions}
          fullWidth
        />
        <FastField
          component={CustomChipSelect}
          name="BIOPYCHOSOCIAL_EVALUATION.SUBSTANCE_ABUSE.NON_PRESCRIBED_DRUGS"
          label="Non-prescription drugs"
          options={substanceAbuseOptions}
          fullWidth
        />
        <FastField
          component={CustomChipSelect}
          name="BIOPYCHOSOCIAL_EVALUATION.SUBSTANCE_ABUSE.OTHER_RECREATIONAL_DRUGS"
          label="Other recreational substances"
          options={substanceAbuseOptions}
          fullWidth
        />
        <FastField
          component={CustomCheckboxWithoutInitialValue}
          name="BIOPYCHOSOCIAL_EVALUATION.SUBSTANCE_ABUSE.REPORTS_INTEREST_TO_REDUCE_USE"
          label="Participant reports interest in exploring options to reduce use."
          fullWidth
        />
        <Field
          component={CustomCheckboxWithoutInitialValue}
          name="BIOPYCHOSOCIAL_EVALUATION.SUBSTANCE_ABUSE.REPORTS_RECEIVING_SUPPORT"
          label="Participant reports currently receiving support for substance use."
          fullWidth
        />
        {(values.BIOPYCHOSOCIAL_EVALUATION.SUBSTANCE_ABUSE.REPORTS_INTEREST_TO_REDUCE_USE ||
          values.BIOPYCHOSOCIAL_EVALUATION.SUBSTANCE_ABUSE.REPORTS_RECEIVING_SUPPORT) && (
          <FastField
            component={CustomTextField}
            name="BIOPYCHOSOCIAL_EVALUATION.SUBSTANCE_ABUSE.SUPPORT_DETAILS"
            label="Support details"
            fullWidth
            multiline
            minRows={4}
          />
        )}
      </SubSection>
      <SubSection title="Family History">
        <Field component={CustomRadioGroup} name="BIOPYCHOSOCIAL_EVALUATION.FAMILY_HISTORY">
          <CustomRadio label="Participant reports no family history of mental illness." value="reports_no" />
          <CustomRadio label="Participant reports family history of mental illness." value="reports_yes" />
        </Field>
        {values.BIOPYCHOSOCIAL_EVALUATION.FAMILY_HISTORY === 'reports_yes' && (
          <>
            <Box mt={2}>
              <Typography variant="h6" color="textSecondary">
                Issues
              </Typography>
              <Grid container columnSpacing={2.5} rowSpacing={0}>
                <Grid item xs={12} md={4}>
                  <FastField
                    component={CustomCheckboxWithoutInitialValue}
                    name="BIOPYCHOSOCIAL_EVALUATION.FAMILY_HISTORY_ISSUES.TRAUMA_OR_PTSD"
                    label="Trauma or PTSD"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FastField
                    component={CustomCheckboxWithoutInitialValue}
                    name="BIOPYCHOSOCIAL_EVALUATION.FAMILY_HISTORY_ISSUES.SUBSTANCE_ABUSE"
                    label="Substance abuse"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FastField
                    component={CustomCheckboxWithoutInitialValue}
                    name="BIOPYCHOSOCIAL_EVALUATION.FAMILY_HISTORY_ISSUES.ANXIETY"
                    label="Anxiety"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FastField
                    component={CustomCheckboxWithoutInitialValue}
                    name="BIOPYCHOSOCIAL_EVALUATION.FAMILY_HISTORY_ISSUES.SCHIZOPHRENIA"
                    label="Schizophrenia"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FastField
                    component={CustomCheckboxWithoutInitialValue}
                    name="BIOPYCHOSOCIAL_EVALUATION.FAMILY_HISTORY_ISSUES.SUICIDE"
                    label="Suicide"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FastField
                    component={CustomCheckboxWithoutInitialValue}
                    name="BIOPYCHOSOCIAL_EVALUATION.FAMILY_HISTORY_ISSUES.DEPRESSION"
                    label="Depression"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FastField
                    component={CustomCheckboxWithoutInitialValue}
                    name="BIOPYCHOSOCIAL_EVALUATION.FAMILY_HISTORY_ISSUES.BIPOLAR_DISORDER"
                    label="Bipolar disorder"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FastField
                    component={CustomCheckboxWithoutInitialValue}
                    name="BIOPYCHOSOCIAL_EVALUATION.FAMILY_HISTORY_ISSUES.ADHD"
                    label="ADHD"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FastField
                    component={CustomCheckboxWithoutInitialValue}
                    name="BIOPYCHOSOCIAL_EVALUATION.FAMILY_HISTORY_ISSUES.INCARCERATION"
                    label="Incarceration"
                  />
                </Grid>
              </Grid>
              {errors.BIOPYCHOSOCIAL_EVALUATION?.FAMILY_HISTORY_ISSUES && (
                <FormHelperText error>{errors.BIOPYCHOSOCIAL_EVALUATION?.FAMILY_HISTORY_ISSUES}</FormHelperText>
              )}
            </Box>
            <Box mt={2}>
              <FastField
                component={CustomTextField}
                name="BIOPYCHOSOCIAL_EVALUATION.FAMILY_HISTORY_DETAILS"
                label="Family history details (if applicable)"
                fullWidth
                multiline
                minRows={4}
              />
            </Box>
          </>
        )}
      </SubSection>
      <SubSection title="Current Living Situation">
        <FastField
          component={CustomTextField}
          name="BIOPYCHOSOCIAL_EVALUATION.CURRENT_LIVING_SITUATION"
          label="Current living situation"
          fullWidth
          multiline
          minRows={4}
        />
      </SubSection>
    </Section>
  )
}
